import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Select, Input, InputNumber, Card, Space, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { FieldInfo, FilterRule } from '../../types';

const { Option } = Select;

interface FilterPanelProps {
  visible: boolean;
  onClose: () => void;
  fieldList: FieldInfo[];
  onApplyFilters: (filters: FilterRule[], logicRelation?: 'and' | 'or') => void;
  onClearFilters: () => void;
  activeFilters: FilterRule[];
  anchorElement: HTMLElement | null;
  initialLogicRelation?: 'and' | 'or';
}

// 操作符配置
const OPERATORS = {
  text: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'contains', label: '包含' },
    { value: 'not_contains', label: '不包含' },
    { value: 'starts_with', label: '开头是' },
    { value: 'ends_with', label: '结尾是' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' }
  ],
  number: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'greater_than', label: '大于' },
    { value: 'greater_than_or_equal', label: '大于等于' },
    { value: 'less_than', label: '小于' },
    { value: 'less_than_or_equal', label: '小于等于' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' }
  ],
  date: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'greater_than', label: '晚于' },
    { value: 'greater_than_or_equal', label: '不早于' },
    { value: 'less_than', label: '早于' },
    { value: 'less_than_or_equal', label: '不晚于' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' }
  ]
} as const;

export const FilterPanel: React.FC<FilterPanelProps> = ({
  visible,
  onClose,
  fieldList,
  onApplyFilters,
  onClearFilters,
  activeFilters,
  anchorElement,
  initialLogicRelation = 'and'
}) => {
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [logicRelation, setLogicRelation] = useState<'and' | 'or'>(initialLogicRelation);

  // 获取字段类型
  const getFieldType = useCallback((fieldName: string): string => {
    if (!fieldName) return 'text';
    
    if (fieldName.toLowerCase().includes('date') || fieldName.toLowerCase().includes('time')) {
      return 'date';
    }
    if (fieldName.toLowerCase().includes('price') || fieldName.toLowerCase().includes('amount') || fieldName.toLowerCase().includes('count')) {
      return 'number';
    }
    
    return 'text';
  }, []);

  // 根据字段类型获取操作符
  const getOperatorsByFieldType = useCallback((fieldType: string) => {
    if (fieldType.includes('number') || fieldType.includes('integer') || fieldType.includes('float')) {
      return OPERATORS.number;
    }
    if (fieldType.includes('date') || fieldType.includes('timestamp')) {
      return OPERATORS.date;
    }
    return OPERATORS.text;
  }, []);

  // 判断操作符是否需要值输入
  const operatorNeedsValue = useCallback((operator: string): boolean => {
    return !['is_empty', 'is_not_empty'].includes(operator);
  }, []);

  // 获取默认字段
  const defaultField = useMemo(() => {
    return fieldList.find(field => field.field !== 'addColumn')?.field || '';
  }, [fieldList]);

  // 同步外部传入的逻辑关系
  useEffect(() => {
    setLogicRelation(initialLogicRelation);
  }, [initialLogicRelation]);
  
  // 初始化筛选条件
  useEffect(() => {
    if (visible) {
      if (activeFilters.length > 0) {
        setFilters([...activeFilters]);
      } else {
        setFilters([{ field: defaultField, operator: 'equals', value: '' }]);
      }
    }
  }, [visible, activeFilters, defaultField]);

  // 计算弹窗位置
  useEffect(() => {
    if (visible && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      const viewportWidth = window.innerWidth;
      const panelWidth = 600;
      let leftPosition = rect.left + scrollLeft;
      
      if (leftPosition + panelWidth > viewportWidth) {
        leftPosition = viewportWidth - panelWidth - 20;
      }
      
      setPosition({
        top: rect.bottom + scrollTop + 8,
        left: Math.max(20, leftPosition)
      });
    }
  }, [visible, anchorElement]);

  // 点击外部关闭
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const panel = document.getElementById('filter-panel');
      const button = anchorElement;
      
      const isInDropdown = target.closest('.ant-select-dropdown') || 
                          target.closest('.ant-select-selector') ||
                          target.closest('.ant-select-selection-search');
      
      if (panel && !panel.contains(target) && 
          button && !button.contains(target) && 
          !isInDropdown) {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose, anchorElement]);

  // 添加筛选条件
  const addFilter = useCallback(() => {
    setFilters(prev => [...prev, { field: defaultField, operator: 'equals', value: '' }]);
  }, [defaultField]);

  // 删除筛选条件
  const removeFilter = useCallback((index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 更新筛选条件
  const updateFilter = useCallback((index: number, key: keyof FilterRule, value: any) => {
    setFilters(prev => {
      const newFilters = [...prev];
      newFilters[index] = { ...newFilters[index], [key]: value };
      
      // 当字段变化时，重置操作符和值
      if (key === 'field') {
        const fieldType = getFieldType(value);
        const operators = getOperatorsByFieldType(fieldType);
        newFilters[index].operator = operators[0]?.value || 'equals';
        newFilters[index].value = '';
      }
      
      // 当操作符变化时，如果新操作符不需要值，则清空值
      if (key === 'operator' && !operatorNeedsValue(value as string)) {
        newFilters[index].value = '';
      }
      
      return newFilters;
    });
  }, [getFieldType, getOperatorsByFieldType, operatorNeedsValue]);

  // 渲染值输入组件
  const renderValueInput = useCallback((filter: FilterRule, index: number): React.ReactNode => {
    if (!operatorNeedsValue(filter.operator)) {
      return null;
    }

    const fieldType = getFieldType(filter.field);
    
    if (fieldType.includes('number') || fieldType.includes('integer')) {
      return (
        <InputNumber
          placeholder="请输入数值"
          value={filter.value as number}
          onChange={(value) => updateFilter(index, 'value', value)}
          style={{ width: 200 }}
        />
      );
    }
    
    if (fieldType.includes('date') || fieldType.includes('timestamp')) {
      return (
        <Input
          type="date"
          placeholder="请选择日期"
          value={filter.value as string}
          onChange={(e) => updateFilter(index, 'value', e.target.value)}
          style={{ width: 200 }}
        />
      );
    }
    
    return (
      <Input
        placeholder="请输入"
        value={filter.value as string}
        onChange={(e) => updateFilter(index, 'value', e.target.value)}
        style={{ width: 200 }}
      />
    );
  }, [getFieldType, operatorNeedsValue, updateFilter]);

  // 应用筛选
  const handleApply = useCallback(() => {
    const validFilters = filters.filter(f => 
      f.field && f.operator && (operatorNeedsValue(f.operator) ? f.value !== '' && f.value !== null && f.value !== undefined : true)
    );
    onApplyFilters(validFilters, logicRelation);
  }, [filters, logicRelation, operatorNeedsValue, onApplyFilters]);

  // 取消操作
  const handleCancel = useCallback(() => {
    setFilters(activeFilters.length > 0 ? [...activeFilters] : [{ field: defaultField, operator: 'equals', value: '' }]);
    onClose();
  }, [activeFilters, defaultField, onClose]);

  if (!visible) return null;

  return (
    <div
      id="filter-panel"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1000,
        width: 600
      }}
    >
      <Card
        style={{
          borderRadius: 8,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e8e8e8'
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* 标题栏 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600, color: '#333333' }}>
            设置筛选条件
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, color: '#666666' }}>符合以下</span>
            <Select
              value={logicRelation}
              onChange={setLogicRelation}
              style={{ width: 80 }}
              size="small"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              <Option value="and">所有</Option>
              <Option value="or">任意</Option>
            </Select>
            <span style={{ fontSize: 14, color: '#666666' }}>条件</span>
            <QuestionCircleOutlined 
              style={{ color: '#999999', cursor: 'pointer' }}
              title="选择'所有'表示需同时满足所有条件，选择'任意'表示满足其中一个条件即可"
            />
          </div>
        </div>

        {/* 条件配置区 */}
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filters.map((filter, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Select
                  placeholder="选择字段"
                  value={filter.field || undefined}
                  onChange={(value: string) => updateFilter(index, 'field', value)}
                  style={{ width: 120 }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  {fieldList
                    .filter(field => field.field !== 'addColumn')
                    .map(field => (
                      <Option key={field.field} value={field.field}>
                        {field.title}
                      </Option>
                    ))}
                </Select>
                
                <Select
                  placeholder="选择条件"
                  value={filter.operator}
                  onChange={(value: string) => updateFilter(index, 'operator', value)}
                  style={{ width: 100 }}
                  disabled={!filter.field}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  {getOperatorsByFieldType(getFieldType(filter.field)).map(op => (
                    <Option key={op.value} value={op.value}>
                      {op.label}
                    </Option>
                  ))}
                </Select>
                
                {renderValueInput(filter, index)}
                
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilter(index)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </div>
            ))}
          </div>

          <Divider style={{ margin: '20px 0' }} />

          {/* 底部操作区 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addFilter}
              style={{
                color: '#666666',
                borderColor: '#d9d9d9'
              }}
            >
              添加条件
            </Button>
            
            <Space size={16}>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button type="primary" onClick={handleApply}>
                确定
              </Button>
            </Space>
          </div>
        </div>
      </Card>
    </div>
  );
};